const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', (carousel) => {
      carousel.container.setAttribute(`data-initialised`, true)
    })
  }
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',')

const ORIGINAL_TABINDEX_ATTRIBUTE = 'data-carousel-original-tabindex'

const setFocusableState = (slide, isHidden) => {
  const focusableElements = slide.querySelectorAll(FOCUSABLE_SELECTOR)

  focusableElements.forEach((element) => {
    if (isHidden) {
      if (!element.hasAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)) {
        const originalTabindex = element.getAttribute('tabindex')
        element.setAttribute(
          ORIGINAL_TABINDEX_ATTRIBUTE,
          originalTabindex ?? '',
        )
      }

      element.setAttribute('tabindex', '-1')

      return
    }

    if (!element.hasAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)) {
      return
    }

    const originalTabindex = element.getAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)

    if (originalTabindex === '') {
      element.removeAttribute('tabindex')
    } else {
      element.setAttribute('tabindex', originalTabindex)
    }

    element.removeAttribute(ORIGINAL_TABINDEX_ATTRIBUTE)
  })
}

const getIsSlideVisible = (carousel, slideIndex) => {
  const details = carousel.track?.details
  const slideDetail = details?.slides?.[slideIndex]

  if (!slideDetail) {
    return details?.rel === slideIndex
  }

  return slideDetail.portion > 0
}

const setSlideA11yState = (carousel) => {
  if (!carousel?.slides?.length) {
    return
  }

  carousel.slides.forEach((slide, index) => {
    const isVisible = getIsSlideVisible(carousel, index)
    const isHidden = !isVisible

    slide.setAttribute('aria-hidden', String(isHidden))
    setFocusableState(slide, isHidden)
  })
}

const setRegionA11y = (carousel, { label }) => {
  const existingLabel = carousel.container.getAttribute('aria-label')
  const effectiveLabel = existingLabel || label

  carousel.container.setAttribute('role', 'region')
  carousel.container.setAttribute('aria-roledescription', 'carousel')

  if (!/carousel/i.test(effectiveLabel)) {
    carousel.container.setAttribute('aria-label', `${effectiveLabel} carousel`)
  } else {
    carousel.container.setAttribute('aria-label', effectiveLabel)
  }

  if (!carousel.container.hasAttribute('tabindex')) {
    carousel.container.setAttribute('tabindex', '0')
  }
}

const setScreenReaderInstructions = (carousel, { instructions }) => {
  if (carousel.container.querySelector('[data-carousel-a11y-instructions]')) {
    return
  }

  if (!carousel.container.id) {
    carousel.container.id = `carousel-${Math.random().toString(36).slice(2, 8)}`
  }

  const instructionsId = `${carousel.container.id}-instructions`
  const instructionElement = document.createElement('span')

  instructionElement.className = 'util-ScreenReaderOnly'
  instructionElement.id = instructionsId
  instructionElement.setAttribute('data-carousel-a11y-instructions', 'true')
  instructionElement.textContent = instructions

  carousel.container.prepend(instructionElement)

  const existingDescribedBy =
    carousel.container.getAttribute('aria-describedby')
  const describedByTokens = (existingDescribedBy || '')
    .split(' ')
    .filter(Boolean)

  if (!describedByTokens.includes(instructionsId)) {
    describedByTokens.push(instructionsId)
    carousel.container.setAttribute(
      'aria-describedby',
      describedByTokens.join(' '),
    )
  }
}

const getActiveSlideIndex = (carousel) => {
  const details = carousel.track?.details

  if (typeof details?.abs === 'number') {
    return details.abs
  }

  if (typeof details?.rel === 'number') {
    return details.rel
  }

  return 0
}

const getSlideAnnouncementText = (slide) => {
  if (!slide) {
    return ''
  }

  const explicitLabel =
    slide.getAttribute('data-carousel-a11y-label') ||
    slide.getAttribute('aria-label')

  if (explicitLabel) {
    return explicitLabel.trim()
  }

  const heading = slide.querySelector('h1, h2, h3, h4, h5, h6')

  if (heading?.textContent) {
    return heading.textContent.trim()
  }

  const text = slide.textContent || ''
  return text.trim().replace(/\s+/g, ' ').slice(0, 120)
}

const getLiveRegion = (carousel) => {
  let liveRegion = carousel.container.querySelector(
    '[data-carousel-a11y-live]',
  )

  if (liveRegion) {
    return liveRegion
  }

  liveRegion = document.createElement('span')
  liveRegion.className = 'util-ScreenReaderOnly'
  liveRegion.setAttribute('data-carousel-a11y-live', 'true')
  liveRegion.setAttribute('aria-live', 'polite')
  liveRegion.setAttribute('aria-atomic', 'true')
  liveRegion.setAttribute('role', 'status')

  carousel.container.appendChild(liveRegion)

  return liveRegion
}

const announceActiveSlide = (carousel, { liveRegion, announcementPrefix }) => {
  const activeSlideIndex = getActiveSlideIndex(carousel)
  const slideCount = carousel.slides?.length || 0
  const activeSlide = carousel.slides?.[activeSlideIndex]

  if (!liveRegion || !slideCount || !activeSlide) {
    return
  }

  const slideText = getSlideAnnouncementText(activeSlide)
  const announcementParts = [
    announcementPrefix,
    `Slide ${activeSlideIndex + 1} of ${slideCount}`,
    slideText,
  ].filter(Boolean)

  const nextMessage = announcementParts.join('. ')

  if (liveRegion.textContent === nextMessage) {
    liveRegion.textContent = ''
  }

  liveRegion.textContent = nextMessage
}

const setKeyboardControls = (carousel) => {
  carousel.container.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      carousel.next()
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      carousel.prev()
    }

    if (event.key === 'Home') {
      event.preventDefault()
      carousel.moveToIdx(carousel.track.details.min)
    }

    if (event.key === 'End') {
      event.preventDefault()
      carousel.moveToIdx(carousel.track.details.maxIdx)
    }
  })
}

/**
 * @param {{
 *  label?: string,
 *  instructions?: string,
 *  announceSlides?: boolean,
 *  announcementPrefix?: string,
 * }} options
 */
const useA11y = ({
  label = 'Content carousel',
  instructions = 'Carousel. Use left and right arrow keys to navigate between slides.',
  announceSlides = false,
  announcementPrefix = '',
} = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let liveRegion

    const updateAccessibilityState = () => {
      setSlideA11yState(carousel)

      if (announceSlides) {
        announceActiveSlide(carousel, {
          liveRegion,
          announcementPrefix,
        })
      }
    }

    carousel.on('created', () => {
      setRegionA11y(carousel, { label })
      setScreenReaderInstructions(carousel, { instructions })
      setKeyboardControls(carousel)

      if (announceSlides) {
        liveRegion = getLiveRegion(carousel)
      }

      updateAccessibilityState()
    })

    carousel.on('slideChanged', updateAccessibilityState)
    carousel.on('updated', updateAccessibilityState)
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', maxIdx === 0)
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', maxIdx === 0)
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = {
  setInitialised,
  useA11y,
  useArrows,
  useAutoplay,
  useDots,
}
